/** The "Fields" dialog (deliverable 18): one editor per field family, a commit per change. */
import type {
  DocumentV1,
  FieldDefV1,
  LinkTypeV1,
  LinkV1,
  PropValueV1,
  QuarantineReason,
} from '@codex/contracts';
import { Button, Dialog } from '@codex/ui';
import { useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { DatedFieldRow } from '../../temporal/field-row.js';
import { valueInputFor } from '../../temporal/value-inputs.js';
import { describeRefusal } from '../model/refusals.js';
import { type PropInput, propText, toPropValue } from '../model/values.js';
import { docQuarantineKey } from './doc-fields.js';
import { BooleanEditor, MultiSelectEditor, SelectEditor } from './editors/ChoiceEditors.js';
import { DatedReadOnly, isDatedField } from './editors/DatedReadOnly.js';
import { ImageEditor } from './editors/ImageEditor.js';
import { ReferenceEditor } from './editors/ReferenceEditor.js';
import type { EditorProps } from './editors/shared.js';
import { LongTextEditor, TextEditor } from './editors/TextEditors.js';
import { usePillServices } from './services.js';

export const REASON_WORDS: Readonly<Record<QuarantineReason, string>> = {
  conversion: 'Kept when the field changed kind',
  retype: 'Kept when the page changed type',
  unknown: 'Kept from an older format',
};

/** The date part of a timestamp, in UTC, so the text is the same wherever it is read. */
const dayOf = (at: string): string => at.slice(0, 10);

export function FieldsDialog({
  doc,
  fields,
  links,
  linkTypes,
  readOnly,
  onClose,
  refreshDoc,
  reload,
}: {
  doc: DocumentV1;
  fields: readonly FieldDefV1[];
  links: readonly LinkV1[];
  linkTypes: readonly LinkTypeV1[];
  readOnly: boolean;
  onClose: () => void;
  refreshDoc: () => Promise<DocumentV1 | undefined>;
  reload: () => void;
}) {
  const services = usePillServices();
  const [drafts, setDrafts] = useState<Readonly<Record<string, string>>>({});
  const [errors, setErrors] = useState<Readonly<Record<string, string>>>({});
  const [searches, setSearches] = useState<Readonly<Record<string, string>>>({});
  const [pending, setPending] = useState<Readonly<Record<string, PropValueV1>>>({});
  const aside = useQuery({
    queryKey: docQuarantineKey(doc.id),
    queryFn: () => services.quarantine(doc.id),
    // Each opening reads the list afresh: a conversion on the type page sets values aside here.
    staleTime: 0,
    retry: false,
  });

  const without = <T,>(record: Readonly<Record<string, T>>, id: string) => {
    const { [id]: _gone, ...rest } = record;
    return rest;
  };
  // Typing again lifts the refusal; an unchanged refused text is not sent a second time.
  const setDraft = (id: string, text: string | undefined) => {
    setDrafts((all) => (text === undefined ? without(all, id) : { ...all, [id]: text }));
    setErrors((all) => without(all, id));
  };

  // The text being written per field: Enter then blur must not send the same text twice.
  const inFlight = useRef<Record<string, string>>({});

  const commit = async (field: FieldDefV1, input: PropInput) => {
    if (typeof input === 'string') {
      if (inFlight.current[field.id] === input) return;
      inFlight.current[field.id] = input;
    }
    const value = toPropValue(field, input);
    setErrors((all) => without(all, field.id));
    if (typeof input !== 'string') setPending((all) => ({ ...all, [field.id]: value }));
    try {
      await services.patchProp(doc.id, field.id, value);
      await refreshDoc();
      setDrafts((all) => without(all, field.id));
    } catch (error) {
      setErrors((all) => ({ ...all, [field.id]: describeRefusal(error) }));
    } finally {
      delete inFlight.current[field.id];
      setPending((all) => without(all, field.id));
    }
  };

  /** Esc: first undo what is being edited; only an Esc that changes nothing closes. */
  const onDialogClose = (reason: 'esc' | 'backdrop' | 'button' | string) => {
    if (reason === 'esc') {
      const active = document.activeElement as HTMLElement | null;
      const id = active?.getAttribute('data-field-id');
      if (id && id in drafts) {
        const field = fields.find((f) => f.id === id);
        const stored = propText(field ? doc.props[field.id] : undefined);
        if (drafts[id] !== stored || id in errors) {
          setDraft(id, undefined);
          setErrors((all) => without(all, id));
          return;
        }
      }
      const searching = active?.getAttribute('data-search');
      if (searching && (searches[searching] ?? '') !== '') {
        setSearches((all) => without(all, searching));
        return;
      }
    }
    onClose();
  };

  const rows = fields.map((field) => {
    const common: EditorProps = {
      field,
      value: field.id in pending ? pending[field.id] : doc.props[field.id],
      error: errors[field.id] ?? null,
      commit: (input) => void commit(field, input),
    };
    // 1.Y: while `chrono` is on, dated and world-date fields are edited here (DECISIONS 341).
    if (isDatedField(field))
      return (
        <DatedFieldRow
          key={field.id}
          docId={doc.id}
          field={field}
          readOnly={readOnly}
          renderValueInput={valueInputFor(field)}
          fallback={<DatedReadOnly field={field} />}
        />
      );
    if (field.kind === 'doc_ref' || field.kind === 'doc_refs')
      return (
        <ReferenceEditor
          key={field.id}
          field={field}
          docId={doc.id}
          links={links}
          linkTypes={linkTypes}
          readOnly={readOnly}
          reload={reload}
          onOpened={onClose}
          query={searches[field.id] ?? ''}
          setQuery={(text) => setSearches((all) => ({ ...all, [field.id]: text }))}
        />
      );
    if (field.kind === 'asset')
      return <ImageEditor key={field.id} {...common} readOnly={readOnly} />;
    if (readOnly) return <ReadOnlyRow key={field.id} field={field} value={common.value} />;
    if (field.kind === 'boolean') return <BooleanEditor key={field.id} {...common} />;
    if (field.kind === 'select') return <SelectEditor key={field.id} {...common} />;
    if (field.kind === 'multi_select') return <MultiSelectEditor key={field.id} {...common} />;
    const text = {
      draft: drafts[field.id],
      setDraft: (t: string | undefined) => setDraft(field.id, t),
    };
    return field.kind === 'long_text' ? (
      <LongTextEditor key={field.id} {...common} {...text} />
    ) : (
      <TextEditor key={field.id} {...common} {...text} />
    );
  });

  const entries = aside.data ?? [];
  return (
    <Dialog
      open
      onClose={onDialogClose}
      title="Fields"
      actions={
        <Button variant="primary" onClick={onClose}>
          Done
        </Button>
      }
    >
      <ul className="cx-pills__fields">
        {rows.map((row) => (
          <li key={row.key}>{row}</li>
        ))}
      </ul>
      {entries.length > 0 && (
        <section className="cx-pills__aside" aria-labelledby="pills-aside">
          <h3 id="pills-aside">Set aside</h3>
          <ul>
            {entries.map((entry) => (
              <li key={entry.id} className="cx-pills__field">
                <span className="cx-pills__label">{entry.fieldLabel}</span>
                <label className="cx-pills__hint">
                  <span className="cx-visually-hidden">Set-aside text of {entry.fieldLabel}</span>
                  <input
                    className="cx-input__el"
                    readOnly
                    value={entry.text ?? '(image)'}
                    onFocus={(event) => event.currentTarget.select()}
                  />
                </label>
                <span className="cx-pills__hint">
                  {REASON_WORDS[entry.reason]}, {dayOf(entry.at)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </Dialog>
  );
}

/** A value shown as text on a page that cannot be edited. */
function ReadOnlyRow({ field, value }: { field: FieldDefV1; value: PropValueV1 | undefined }) {
  const text =
    field.kind === 'boolean'
      ? value === true
        ? 'Yes'
        : value === false
          ? 'No'
          : ''
      : propText(value);
  return (
    <div className="cx-pills__field">
      <span className="cx-pills__label">{field.label}</span>
      <span>{text === '' ? 'Not set.' : text}</span>
    </div>
  );
}
